import {
	IconBrandLinkedin,
	IconBrandX,
	IconChevronLeft,
	IconChevronRight,
	IconCopy,
	IconDownload,
	IconShare,
} from "@tabler/icons-react"
import { useReducedMotion } from "motion/react"
import { type FC, useEffect, useRef, useState } from "react"

import { type Milestone } from "@forento/core/models/milestone"

import Button from "~/components/Button"
import { DefaultModal, ModalButtons, ModalContentPadding, ModalText, ModalTitle } from "~/components/Modal"
import ToggleGroup from "~/components/ToggleGroup"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform } from "~/contexts/UserContext"
import { showConfetti } from "~/utilities/celebration"
import { downloadBlob } from "~/utilities/element"

import MilestoneCard from "./MilestoneCard"
import { getMilestoneCopy, type MilestoneFormat, renderMilestoneImage, trackMilestoneShare } from "./utilities"

type Props = {
	milestones: Milestone[]
	index: number
	isNew: boolean
	onIndexChange(index: number): void
	onClose(): void
}

const ShareMilestoneModal: FC<Props> = ({ milestones, index, isNew, onIndexChange, onClose }) => {
	const platform = usePlatform()!
	const toast = useToast()
	const prefersReducedMotion = useReducedMotion()

	const cardRef = useRef<HTMLDivElement>(null)
	const [format, setFormat] = useState<MilestoneFormat>("square")
	const [isRendering, setRendering] = useState(false)

	const milestone = milestones[index] ?? null
	const hasPrevious = index > 0
	const hasNext = index < milestones.length - 1

	useEffect(() => {
		if (milestones.length === 0 || !isNew || prefersReducedMotion) return

		const timeout = window.setTimeout(async () => {
			if (cardRef.current !== null) await showConfetti(cardRef.current)
		}, 300)
		return () => window.clearTimeout(timeout)
	}, [milestones, isNew, prefersReducedMotion])

	useEffect(() => {
		if (milestones.length < 2) return

		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === "ArrowLeft" && hasPrevious) onIndexChange(index - 1)
			else if (event.key === "ArrowRight" && hasNext) onIndexChange(index + 1)
		}
		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [milestones.length, index, hasPrevious, hasNext, onIndexChange])

	if (milestone === null)
		return (
			<DefaultModal onClose={onClose} isOpen={false}>
				{null}
			</DefaultModal>
		)

	const copy = getMilestoneCopy(milestone, platform.name)
	const url = `https://${platform.domain.landingPage ?? platform.domain.platform}`
	const fileName = `${platform.name} - ${copy.title}.png`.replace(/[\\/:*?"<>|]/g, "")
	const canShareFiles =
		typeof navigator.canShare === "function" &&
		navigator.canShare({ files: [new File([], fileName, { type: "image/png" })] })

	async function renderImage() {
		if (cardRef.current === null) return null
		setRendering(true)
		try {
			return await renderMilestoneImage(cardRef.current)
		} catch (error) {
			console.error(error)
			toast.showError("Failed to create the image! Please try again later.")
			return null
		} finally {
			setRendering(false)
		}
	}

	async function handleDownload() {
		const image = await renderImage()
		if (image === null) return
		downloadBlob(image, fileName)
		trackMilestoneShare(milestone!, "download")
	}

	async function handleShare() {
		const image = await renderImage()
		if (image === null) return
		try {
			await navigator.share({
				files: [new File([image], fileName, { type: "image/png" })],
				text: `${copy.caption}\n${url}`,
			})
			trackMilestoneShare(milestone!, "native")
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") return
			console.error(error)
			toast.showError("Failed to share the image! Please download it instead.")
		}
	}

	async function handlePost(network: "x" | "linkedin") {
		// The share intents only accept text, so the image is downloaded for the creator to attach
		const shareWindow = window.open("", "_blank")
		const image = await renderImage()
		if (image === null) {
			shareWindow?.close()
			return
		}
		downloadBlob(image, fileName)

		const text = encodeURIComponent(copy.caption)
		const shareUrl =
			network === "x"
				? `https://x.com/intent/post?text=${text}&url=${encodeURIComponent(url)}`
				: `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(`${copy.caption}\n${url}`)}`
		if (shareWindow !== null) shareWindow.location.href = shareUrl
		else window.open(shareUrl, "_blank")
		trackMilestoneShare(milestone!, network)
	}

	async function handleCopyCaption() {
		await navigator.clipboard.writeText(`${copy.caption}\n${url}`)
		toast.showSuccess("Caption copied!")
		trackMilestoneShare(milestone!, "copy-caption")
	}

	return (
		<DefaultModal onClose={onClose} isOpen width={560}>
			<ModalTitle>
				{!isNew
					? "Share your milestone"
					: milestones.length > 1
						? `You unlocked ${milestones.length} milestones 🎉`
						: "You unlocked a milestone 🎉"}
			</ModalTitle>
			<ModalText>
				{isNew
					? `${copy.caption} Celebrate it with your audience and share the news on social media.`
					: "Download the image or share it directly to your socials."}
			</ModalText>
			<ModalContentPadding className="flex flex-col items-center gap-4">
				<ToggleGroup
					options={[
						{ id: "square", title: "Post" },
						{ id: "story", title: "Story" },
					]}
					selectedId={format}
					onChange={id => setFormat(id as MilestoneFormat)}
				/>
				<div className="flex w-full items-center justify-center gap-3">
					{milestones.length > 1 && (
						<button
							type="button"
							onClick={() => onIndexChange(index - 1)}
							disabled={!hasPrevious}
							aria-label="Previous milestone"
							className={navigationButtonClass}
						>
							<IconChevronLeft className="size-4" />
						</button>
					)}
					{/* Widths divide 1080 evenly so the exported image is exactly 1080x1080 or 1080x1920 */}
					<div className="min-w-0 overflow-hidden rounded-2xl">
						<MilestoneCard
							ref={cardRef}
							milestone={milestone}
							format={format}
							className={format === "square" ? "w-90 max-w-full" : "w-[270px] max-w-full"}
						/>
					</div>
					{milestones.length > 1 && (
						<button
							type="button"
							onClick={() => onIndexChange(index + 1)}
							disabled={!hasNext}
							aria-label="Next milestone"
							className={navigationButtonClass}
						>
							<IconChevronRight className="size-4" />
						</button>
					)}
				</div>
				{milestones.length > 1 && (
					<span className="-mt-2 text-xs font-medium text-gray-500 tabular-nums">
						{index + 1} of {milestones.length}
					</span>
				)}
				<div className="flex flex-wrap justify-center gap-2">
					{canShareFiles && (
						<Button variant="primary" onClick={handleShare} isDisabled={isRendering}>
							<IconShare className="size-4" />
							Share
						</Button>
					)}
					<Button
						variant={canShareFiles ? "secondary" : "primary"}
						onClick={handleDownload}
						isLoading={isRendering}
					>
						<IconDownload className="size-4" />
						Download image
					</Button>
					<Button variant="secondary" onClick={() => handlePost("x")} isDisabled={isRendering}>
						<IconBrandX className="size-4" />
						Post on X
					</Button>
					<Button variant="secondary" onClick={() => handlePost("linkedin")} isDisabled={isRendering}>
						<IconBrandLinkedin className="size-4" />
						Post on LinkedIn
					</Button>
					<Button variant="secondary" onClick={handleCopyCaption}>
						<IconCopy className="size-4" />
						Copy caption
					</Button>
				</div>
				<p className="text-center text-xs text-gray-500">
					Posting on X or LinkedIn downloads the image so you can attach it to your post.
				</p>
			</ModalContentPadding>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose}>
					Close
				</Button>
			</ModalButtons>
		</DefaultModal>
	)
}

const navigationButtonClass =
	"flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-component-border bg-white text-gray-700 transition hover:bg-component-background focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-white"

export default ShareMilestoneModal
